import { useParams } from 'react-router-dom';
import { pickRequiredParams } from 'hooks/useRequiredParams';

import type { AppPathTemplate } from './routePaths';

/**
 * Route params for recruiter-app templates. Placeholder names match the
 * URL registry (`:folder_id`, `:campaign_id`, …), which is also what
 * React Router now mounts.
 */
export type AppPathParams = {
  folder_id?: string;
  campaign_id?: string;
  candidate_id?: string;
  call_id?: string;
  note_id?: string;
  main_tab?: string;
  sub_tab?: string;
  dashboard_id?: string;
};

/**
 * Every registry param, all optional. For shared components rendered under
 * routes with different params; a page reads its own with `useRouteParams`.
 */
export function useAppParams(): AppPathParams {
  return useParams<AppPathParams>();
}

/** `:param` names in one template, e.g. `'note_id'` for `/notes/:note_id`. */
export type TemplateParamName<S extends string> =
  S extends `${string}:${infer P}/${infer R}`
    ? P | TemplateParamName<`/${R}`>
    : S extends `${string}:${infer P}`
      ? P
      : never;

/** Param names every template in `T` contains. */
type CommonParamName<T extends readonly string[]> = T extends readonly [
  infer Head extends string,
  ...infer Tail extends readonly string[],
]
  ? Tail extends readonly []
    ? TemplateParamName<Head>
    : TemplateParamName<Head> & CommonParamName<Tail>
  : never;

/**
 * Params of a page mounted at the templates `T`: those in every template are
 * required, those in only some are optional.
 */
export type RouteParams<T extends readonly string[]> = Readonly<
  Record<CommonParamName<T>, string> &
    Partial<
      Record<Exclude<TemplateParamName<T[number]>, CommonParamName<T>>, string>
    >
>;

type NonEmptyTemplates = readonly [string, ...string[]];

function templateHasParam(template: string, name: string): boolean {
  return template.split('/').includes(`:${name}`);
}

function isTemplateParamName<T extends NonEmptyTemplates>(
  templates: T,
  name: string
): name is TemplateParamName<T[number]> {
  return templates.some((template) => templateHasParam(template, name));
}

function isCommonParamName<T extends NonEmptyTemplates>(
  templates: T,
  name: string
): name is CommonParamName<T> {
  return templates.every((template) => templateHasParam(template, name));
}

function isOptionalParamName<T extends NonEmptyTemplates>(
  templates: T,
  name: string
): name is Exclude<TemplateParamName<T[number]>, CommonParamName<T>> {
  return (
    isTemplateParamName(templates, name) && !isCommonParamName(templates, name)
  );
}

function paramNamesOf(template: string): string[] {
  return template
    .split('/')
    .filter((segment) => segment.startsWith(':'))
    .map((segment) => segment.slice(1));
}

/**
 * Pick the params of a page mounted at `templates` from the router's params,
 * throwing `MissingRouteParamError` when a param every template contains is
 * missing or empty. Pure so it can be tested without a router.
 */
export function pickRouteParams<const T extends NonEmptyTemplates>(
  params: Readonly<Partial<Record<string, string>>>,
  templates: T
): RouteParams<T> {
  const names = [...new Set(templates.flatMap(paramNamesOf))];
  const required = names.filter((name): name is CommonParamName<T> =>
    isCommonParamName(templates, name)
  );
  const optional: Partial<
    Record<Exclude<TemplateParamName<T[number]>, CommonParamName<T>>, string>
  > = {};
  for (const name of names) {
    const value = params[name];
    if (isOptionalParamName(templates, name) && value) {
      optional[name] = value;
    }
  }
  return { ...optional, ...pickRequiredParams(params, required) };
}

/**
 * Params of the current page, typed from the registry templates it mounts at
 * (every entry for it in `app-shell/routeMounts.ts`):
 *
 *   const { note_id } = useRouteParams(paths.NOTE);
 *   const { folder_id } = useRouteParams(paths.JOBS, paths.JOBS_FOLDER);
 *
 * A param in every template is a required `string`; a missing one throws
 * (rendered by the router's `errorElement`) instead of flowing through the
 * page as `undefined`. A param in only some templates is optional.
 */
export function useRouteParams<
  const T extends readonly [AppPathTemplate, ...AppPathTemplate[]],
>(...templates: T): RouteParams<T> {
  return pickRouteParams(useParams(), templates);
}
